(function() {
  var MetricsCalculator = require('../js_maps/metrics_calculator.js');

  var Data, DataSlice, DataPartition;
  /**
   * simple class to encapsulate "data", which is points and their
   * metadata, which is wrapped up in a metrics calculator
   * having this class allows multiple sample graphs to share the same
   * data and subscribe to its changes
   * @class Data
   * @extends Backbone.Events
   */
  Data = function(points, metrics) {
    this._points = points;
    this.count = points.length;
    this._metrics = metrics;
    return this;
  }
  /**
   * pointsAround objects have lowI, highI, closestI, and pct. 
   * we only need lowI & pct to reconstruct
   */
  var pointsAroundPctTruncationFactor = 1000;
  var pointsAroundMetricTruncationFactor = 100;
  Data.serializePointsAround = function(pointsAround, metric) {
    if(!metric)
      return [pointsAround.lowI, Math.round(pointsAround.pct * pointsAroundPctTruncationFactor) / pointsAroundPctTruncationFactor];
    else
      return Math.round(pointsAround.estimatedPoint[metric] * pointsAroundMetricTruncationFactor) / pointsAroundMetricTruncationFactor;
  }

  _.extend(Data.prototype, Backbone.Events);
  /**
   * Setter/getter for points, an array of data points. Fires
   * points:change event when setting.
   * @param {Array<rwgps.TrackPoint>} pointsOrI (optional) Array of points to set or index of point to get
   * @return {Array<rwgps.TrackPoint>} The current points array.
   */
  Data.prototype.points = function(pointsOrI) {
    if(pointsOrI !== undefined) {
      if(_.isNumber(pointsOrI)) 
        return this._points[pointsOrI];
      var points = pointsOrI;

      // if the first point has time, and the last one does not, lop
      // it off! i've seen this in a trip or two, there is probably a
      // more elegant way of dealing with it but this is what we have
      // for now...
      if(points[0] && points[0].time && !points[points.length-1].time) {
        points.pop();
      }
      
      this._points = points;
      this.count = points.length;
      delete this._metricsCalculator;
      this.trigger('points:change');
    }
    return this._points;
  };
  Data.prototype.seek = function(startI, key, amount, step) {
    if(!step) step = 1;
    var hardStop = step > 0 ? this.count-1 : 0,
        i = startI,
        pts = this.points(),
        refValue = pts[startI][key];
    while(i != hardStop) {
      if(Math.abs(pts[i][key] - refValue) >= amount) {
        return i;
      }
      i += step;
    }
    return i;
  };
  Data.prototype.pointsAroundX = function(scaledX, xAxisType, data, ptOffset) {
    if(!data) data = this.points();

    // binary search for nearest point
    var i, dataX, low = 0,
        high = data.length-1;
    while(low!=high) {
      i = low+Math.floor((high-low)/2);
      dataX = data[i][xAxisType];
      if(scaledX < dataX) high = i;
      else if(scaledX > dataX) {
        // break if point is closer to the value we're throwing out than the value that will be the new low
        if(scaledX - dataX < data[i+1][xAxisType] - scaledX) {
          break;
        }
        low = ++i;
      }
      else {
        break; // found exact match.
      }
    }

    // figure out bounding points and %
    dataX = data[i][xAxisType];
    if(scaledX <= dataX) {
      low = i - 1;
      high = i;
    } else {
      low = i;
      high = i + 1;
    }  
    if(i == 0) {
      low = i;
      high = i + 1;
    } else if(i == data.length-1) {
      low = i - 1;
      high = i;
    }
    var pct = (scaledX - data[low][xAxisType]) / (data[high][xAxisType] - data[low][xAxisType]);
    if(data.length == 1) i = 0;

    // if we're zoomed in need to offset our i so its absolute not relative to selected slice
    if(ptOffset) {
      i += ptOffset;
      low += ptOffset;
      high += ptOffset;
    }

    // stub out point with linearly interpolated values
    var estimatedPoint = this.interpolatePoint(low, pct);

    // return a pointsAround object
    return {
      x: scaledX,
      closestI: i,
      lowI: low,
      highI: high,
      pct: pct,
      estimatedPoint: estimatedPoint
    };
  };
  Data.prototype.metricsKeys = function() { 
    if(!this._metricsKeys) {
      this._metricsKeys = this.metricsCalculator().enabledMetrics();
    }
    return this._metricsKeys;
  };
  /**
   * Lazy load an rwgps.MetricsCalculator instance for the current
   * points.
   * @return {rwgps.MetricsCalculator} Instantiated with this.points() and calculate() is already called.
   */
  Data.prototype.metricsCalculator = function() {
    if(!this._metricsCalculator) {
      delete this._metricsKeys;
      this._metricsCalculator = new MetricsCalculator(this, false, this._metrics);
      this._metricsCalculator.calculate(true);
    }
    return this._metricsCalculator;
  };
  Data.prototype.metrics = function() {
    return this.metricsCalculator().metrics();
  };

  Data.prototype.partitionByDistanceFraction = function(frac) {
    this.metricsCalculator().calculate();
    var segmentDistance = this.metricsCalculator().metrics().distance * frac,
        distOffset = segmentDistance,
        points = this.points(),
        partitionIndexes = [],
        lastPt;
    if(frac < 1) {
      for(var i=0,len=points.length; i < len; i++) {
        if(points[i].distance >= distOffset) {
          lastPt = points[i-1];
          var pct = (distOffset-lastPt.distance)/(points[i].distance-lastPt.distance);
          if(pct == 1) {
            pct = 0;
            ++i;
          }
          partitionIndexes.push([i-1, pct]);
          distOffset += segmentDistance;
        }
      }
      // if the last split index cuts the data leaving a section that is only a 10th or less the size of our other slices go ahead and avoid splitting it
      if((points[points.length-1].distance - points[partitionIndexes[partitionIndexes.length-1][0]].distance) / segmentDistance <= frac/10) partitionIndexes.pop();
    }
    var part = new DataPartition(this, partitionIndexes);
    return part;
  };

  Data.prototype.slice = function(i, count, pct1, pct2) {
    return new DataSlice(this, i, count, pct1, pct2);
  };

  // i is where to start, pct is how far to go forward
  Data.prototype.interpolatePoint = function (i, pct) {
    var epOpts = {},
      left = this.points(i),
      right = this.points(i + 1);
    _.each(this.metricsKeys().concat('time', 'distance', 'providedDistance'), function (metric) {
      if (_.isNumber(left[metric]) && _.isNumber(right[metric]))
        epOpts[metric] = left[metric] + pct * (right[metric] - left[metric]);
    });
    // interpolate x/y
    epOpts.point = {
      lat: (right.point.lat - left.point.lat) * pct + left.point.lat,
      lng: (right.point.lng - left.point.lng) * pct + left.point.lng
    };
    const leftHasSurface = left.surface !== undefined
    const rigthHasSurface = right.surface !== undefined
    if (leftHasSurface && rigthHasSurface) {
      epOpts.surface = Math.round(pct) === 1
        ? right.surface
        : left.surface
    } else if (leftHasSurface) {
      epOpts.surface = left.surface;
    } else if (rigthHasSurface) {
      epOpts.surface = right.surface;
    }

    return new rwgps.TrackPoint(epOpts)
  };

  DataSlice = function(graphOrData, i, count, pct1, pct2) {
    var data, lastI;
    if(graphOrData.canvasId) // its a graph
      data = graphOrData._data;
    else
      data = graphOrData;

    // cannot create a dataslice that selects beyond the bounds of data
    // if an invalid dataslice is requested, return best attempt
    if(data.count < i + count) {
      // need at least 2 points for a slice (for calculating distance etc)
      if(i > data.count - 2) {
        i = data.count - 2;
      }
      count = data.count - i;
      pct2 = 0;
    }

    lastI = i + count - 1;

    // interpolation if required
    if(_.isNumber(pct1) && pct1 > 0) 
      this.left = data.interpolatePoint(i, pct1);
    else {
      pct1 = 0;
      this.left = data.points(i);
    }
    if(_.isNumber(pct2) && pct2 > 0) {
      this.right = data.interpolatePoint(i+count-1, pct2);
      count++;
    } else {
      pct2 = 0;
      this.right = data.points(lastI);
    }

    this.i = i;
    this.count = count;
    this._parentData = data;
    this.pct1 = pct1;
    this.pct2 = pct2;
    this.lastI = lastI;
  };
  DataSlice.prototype.serialize = function(metric) {
    if(!metric) 
      return [this.i, this.count];
    else
      return [Math.round(this.firstPoint()[metric] * 100) / 100,
              Math.round(this.lastPoint()[metric] * 100) / 100];

  };

  // more efficient way of pulling point, no slice involved
  DataSlice.prototype.pointByIndex = function(i) {
    return this.points(i);
  };
  DataSlice.prototype.firstPoint = function() {
    return this.left;
  };
  DataSlice.prototype.lastPoint = function() {
    return this.right;
  };
  DataSlice.prototype.buffer = function(key) {
    var diff = this.lastPoint()[key] - this.firstPoint()[key];
    var bufferAmt = diff * 0.35;
    var i = this._parentData.seek(this.i, key, bufferAmt, -1);
    var lastI = this._parentData.seek(this.lastI, key, bufferAmt);
    // and since we require a count to build a slice, calc that 
    var count = lastI - i + 1;
    return new DataSlice(this._parentData, i, count);
  };
  DataSlice.prototype.points = function (i) {
    // proxy request up to data with offset, efficient!
    if(i !== undefined) {
      if(i === 0) return this.left;
      if(i === this.count-1) return this.right;
      return this._parentData.points(i + this.i);
    }
    
    // lazy copy selected data in, avoid this if possible
    if(!this._data) {
      var pts = [this.left], offset = this.i;
      for(var x=offset+1,
              d=this._parentData.points(),
              lastI=this.lastI; 
          x <= lastI; x++) {
        pts.push(d[x]);
      }
      if(this.pct2 > 0) pts.push(this.right);
      this._data = pts;
    }
    return this._data;
  };
  DataSlice.prototype.data = DataSlice.prototype.points;
  DataSlice.prototype.dataObj = function() {
    if(!this._dataObj) {
      this._dataObj = new Data(this.points());
    }
    return this._dataObj;
  };
  DataSlice.prototype.metricsCalculator = function () {
    if(!this._metricsCalculator) {
      this._metricsCalculator = new MetricsCalculator(this, true);
      this._metricsCalculator.calculate();
    }
    return this._metricsCalculator;
  };
  DataSlice.prototype.metrics = function() {
    return this.metricsCalculator().metrics();
  };

  DataPartition = function(data, divideOnPositions) {
    this.data = data;
    var slices = this._slices = [];
    if(divideOnPositions.length == 0) 
      slices.push(new DataSlice(data, 0, this.data.points().length));
    else {
      for(var i=0,firstPos=[0,0]; i < divideOnPositions.length; i++) {
        slices.push(new DataSlice(data, firstPos[0], divideOnPositions[i][0]-firstPos[0]+1, firstPos[1], divideOnPositions[i][1]));
        firstPos = divideOnPositions[i];
      }
      slices.push(new DataSlice(data, firstPos[0], data.points().length-firstPos[0], firstPos[1]));
    }
  };
  DataPartition.prototype = {
    slices: function() { return this._slices; },
    slice: function(i) { return this._slices[i]; }
  };

  module.exports = {
    Data: Data,
    DataSlice: DataSlice,
    DataPartition: DataPartition
  };
})();



// WEBPACK FOOTER //
// ./public/javascripts/rwgps/data.js